import {
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useMemo,
  useRef,
  type DOMAttributes,
  useCallback,
} from 'react';

import { focusWithoutScrolling } from '../../buttons/button/utils/focus-without-scrolling.js';
import {
  isVirtualPointerEvent,
  isVirtualClick,
} from '../../buttons/button/utils/is-virtual-event.js';
import type { PointerType } from '../../core/types/pointer.js';

// #region Types
interface FocusableElement extends Element, HTMLOrSVGElement {}

interface GlobalListeners {
  addGlobalListener<K extends keyof DocumentEventMap>(
    el: EventTarget,
    type: K,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listener: (this: Document, ev: DocumentEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addGlobalListener(
    el: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeAllGlobalListeners(): void;
}

interface PressEventResult {
  /** Props to spread on the target element. */
  pressProps: Pick<
    DOMAttributes<Element & HTMLOrSVGElement>,
    | 'onKeyDown'
    | 'onKeyUp'
    | 'onClick'
    | 'onMouseDown'
    | 'onPointerDown'
    | 'onPointerUp'
    | 'onDragStart'
  >;
}

interface PressState {
  isPressed: boolean;
  ignoreEmulatedMouseEvents: boolean;
  ignoreClickAfterPress: boolean;
  didFirePressStart: boolean;
  activePointerId: number | null;
  target: FocusableElement | null;
  isOverTarget: boolean;
  pointerType: PointerType;
}

interface Rect {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface EventPoint {
  clientX: number;
  clientY: number;
  width?: number;
  height?: number;
  radiusX?: number;
  radiusY?: number;
}

/**
 * @internal
 */
export interface PressProps {
  /** Handler that is called when the press is released over the target. */
  onPress?: () => void;
  /** Whether the press events should be disabled. */
  isDisabled?: boolean;
}

// #endregion Types

// #region Utility functions
const modifiedElementMap = new WeakMap<Element, string>();

function disableTextSelection(target?: Element) {
  if (target instanceof HTMLElement || target instanceof SVGElement) {
    // Store the target's original user-select and change to user-select: none
    modifiedElementMap.set(target, target.style.userSelect);
    target.style.userSelect = 'none';
  }
}

function restoreTextSelection(target?: Element) {
  if (target instanceof HTMLElement || target instanceof SVGElement) {
    // If not iOS, restore the target's original user-select if any
    // Ignore state since it doesn't apply for non iOS
    if (target && modifiedElementMap.has(target)) {
      const targetOldUserSelect = modifiedElementMap.get(target);

      if (target.style.userSelect === 'none') {
        target.style.userSelect = targetOldUserSelect as string;
      }

      if (target.getAttribute('style') === '') {
        target.removeAttribute('style');
      }
      modifiedElementMap.delete(target);
    }
  }
}

// Code copied from react-aria
// https://github.com/adobe/react-spectrum/blob/main/packages/@react-aria/interactions/src/usePress.ts
const nonTextInputTypes = new Set([
  'checkbox',
  'radio',
  'range',
  'color',
  'file',
  'image',
  'button',
  'submit',
  'reset',
]);

function useGlobalListeners(): GlobalListeners {
  const globalListeners = useRef(new Map());
  const addGlobalListener = useCallback(
    (
      eventTarget: HTMLElement,
      type: string,
      listener: EventListenerOrEventListenerObject,
      options: boolean | AddEventListenerOptions | undefined,
    ) => {
      // Make sure we remove the listener after it is called with the `once` option.
      const fn = (options as AddEventListenerOptions)?.once
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (...args: any) => {
            globalListeners.current.delete(listener);
            (listener as EventListener)({ ...args });
          }
        : listener;
      globalListeners.current.set(listener, { type, eventTarget, fn, options });
      eventTarget.addEventListener(type, listener, options);
    },
    [],
  );
  const removeGlobalListener = useCallback(
    (
      eventTarget: HTMLElement,
      type: string,
      listener: EventListenerOrEventListenerObject,
      options: boolean | AddEventListenerOptions | undefined,
    ) => {
      const fn = globalListeners.current.get(listener)?.fn || listener;
      eventTarget.removeEventListener(type, fn, options);
      globalListeners.current.delete(listener);
    },
    [],
  );
  const removeAllGlobalListeners = useCallback(() => {
    globalListeners.current.forEach((value, key) => {
      removeGlobalListener(value.eventTarget, value.type, key, value.options);
    });
  }, [removeGlobalListener]);

  useEffect(() => {
    return removeAllGlobalListeners;
  }, [removeAllGlobalListeners]);

  return { addGlobalListener, removeAllGlobalListeners };
}

function isValidInputKey(target: HTMLInputElement, key: string) {
  // Only space should toggle checkboxes and radios, not enter.
  return target.type === 'checkbox' || target.type === 'radio'
    ? key === ' '
    : nonTextInputTypes.has(target.type);
}

function isHTMLAnchorLink(target: Element): boolean {
  return target.tagName === 'A' && target.hasAttribute('href');
}

function isValidKeyboardEvent(
  event: KeyboardEvent,
  currentTarget: Element,
): boolean {
  const { key, code } = event;
  const element = currentTarget as HTMLElement;
  const role = element.getAttribute('role');
  // Accessibility for keyboards. Space and Enter only.
  return (
    (key === 'Enter' || key === ' ' || code === 'Space') &&
    !(
      (element instanceof HTMLInputElement && !isValidInputKey(element, key)) ||
      element instanceof HTMLTextAreaElement ||
      element.isContentEditable
    ) &&
    // A link with a valid href should be handled natively,
    // unless it also has role='button' and was triggered using Space.
    (!isHTMLAnchorLink(element) || (role === 'button' && key !== 'Enter')) &&
    // An element with role='link' should only trigger with Enter key
    !(role === 'link' && key !== 'Enter')
  );
}

function shouldPreventDefault(target: Element) {
  // We cannot prevent default if the target is a draggable element.
  return !(target instanceof HTMLElement) || !target.draggable;
}

function shouldPreventDefaultKeyboard(target: Element, key: string) {
  if (target instanceof HTMLInputElement) {
    return !isValidInputKey(target, key);
  }

  if (target instanceof HTMLButtonElement) {
    return target.type !== 'submit';
  }

  return true;
}

function getPointClientRect(point: EventPoint): Rect {
  const pointOffsetX =
    point.width !== undefined ? point.width / 2 : point.radiusX;
  const pointOffsetY =
    point.height !== undefined ? point.height / 2 : point.radiusY;
  const offsetX = pointOffsetX || 0;
  const offsetY = pointOffsetY || 0;

  return {
    top: point.clientY - offsetY,
    right: point.clientX + offsetX,
    bottom: point.clientY + offsetY,
    left: point.clientX - offsetX,
  };
}

function areRectanglesOverlapping(a: Rect, b: Rect) {
  // check if they cannot overlap on x axis
  if (a.left > b.right || b.left > a.right) {
    return false;
  }
  // check if they cannot overlap on y axis
  if (a.top > b.bottom || b.top > a.bottom) {
    return false;
  }
  return true;
}

function isOverTarget(point: EventPoint, target: Element) {
  const rect = target.getBoundingClientRect();
  const pointRect = getPointClientRect(point);
  return areRectanglesOverlapping(rect, pointRect);
}
// #endregion Utility functions

/**
 * This hook is largely inspired by https://github.com/adobe/react-spectrum/blob/2a1c28b8370f4a19e4bdb92e0daf14d07990f27a/packages/%40react-aria/interactions/src/usePress.ts
 * It handles mouse, touch, keyboard events, as well as screen readers.
 * It internally manages the state of the press interaction, such as detecting when the button is pressed and released.
 * An interaction starts when the user presses down with a mouse or their finger on the target, and ends when they move the pointer off the target.
 * If the pointer is released over the target, then the onPress callback is triggered, unless isDisabled is true.
 * More information about its features: https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/interactions/docs/usePress.mdx
 * @deprecated Will be removed.
 * @internal
 */
export function usePress(props: PressProps): PressEventResult {
  const { onPress, isDisabled } = props;

  const propsRef = useRef<PressProps>({
    onPress,
    isDisabled,
  });

  // Update propsRef
  useEffect(() => {
    propsRef.current = {
      onPress,
      isDisabled,
    };
  }, [isDisabled, onPress]);

  const pressStateRef = useRef<PressState>({
    isPressed: false,
    ignoreEmulatedMouseEvents: false,
    ignoreClickAfterPress: false,
    didFirePressStart: false,
    activePointerId: null,
    target: null,
    isOverTarget: false,
    pointerType: null,
  });

  const { addGlobalListener, removeAllGlobalListeners } = useGlobalListeners();

  const pressProps = useMemo(() => {
    const state = pressStateRef.current;
    let stateTarget = state.target as FocusableElement;

    const triggerPressStart = () => {
      const { isDisabled } = propsRef.current;
      if (isDisabled || state.didFirePressStart) {
        return;
      }

      state.didFirePressStart = true;
    };

    const triggerPressEnd = (wasPressed = true) => {
      const { onPress, isDisabled } = propsRef.current;
      if (!state.didFirePressStart) {
        return;
      }

      state.ignoreClickAfterPress = true;
      state.didFirePressStart = false;

      if (onPress && wasPressed && !isDisabled) {
        onPress();
      }
    };

    const triggerPressUp = () => {
      const { isDisabled } = propsRef.current;
      if (isDisabled) {
        return;
      }
    };

    const cancel = () => {
      if (state.isPressed) {
        if (state.isOverTarget) {
          triggerPressEnd(false);
        }
        state.isPressed = false;
        state.isOverTarget = false;
        state.activePointerId = null;
        state.pointerType = null;
        removeAllGlobalListeners();
        restoreTextSelection(stateTarget);
      }
    };

    const pressProps: PressEventResult['pressProps'] = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onKeyDown(e: any) {
        // Skip press handling when modifier keys are held.
        if (e.metaKey || e.ctrlKey || e.altKey) {
          return;
        }

        const currentTarget = e.currentTarget as FocusableElement;
        if (
          isValidKeyboardEvent(e.nativeEvent, currentTarget) &&
          isValidKeyboardEvent(e, currentTarget) &&
          currentTarget.contains(e.target as Element)
        ) {
          if (shouldPreventDefaultKeyboard(e.target as Element, e.key)) {
            e.preventDefault();
          }
          e.stopPropagation();

          // If the event is repeating, it may have started on a different element
          // after which focus moved to the current element. Ignore these events and
          // only handle the first key down event.
          if (!state.isPressed && !e.repeat) {
            state.target = e.currentTarget as FocusableElement;
            stateTarget = state.target;
            state.isPressed = true;
            state.pointerType = 'keyboard';
            triggerPressStart();

            // Focus may move before the key up event, so register the event on the document
            // instead of the same element where the key down event occurred.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            addGlobalListener(document, 'keyup', onKeyUp as any, false);
          }
        } else if (e.key === 'Enter' && isHTMLAnchorLink(e.currentTarget)) {
          // If the target is a link, we won't have handled this above because we want the default
          // browser behavior to open the link when pressing Enter. But we still need to prevent
          // default so that elements above do not also handle it (e.g. table row).
          e.stopPropagation();
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onKeyUp(e: any) {
        const currentTarget = e.currentTarget as FocusableElement;

        if (
          isValidKeyboardEvent(e.nativeEvent, currentTarget) &&
          !e.repeat &&
          currentTarget.contains(e.target as Element)
        ) {
          triggerPressUp();
        }
      },
      onClick(e: MouseEvent) {
        const currentTarget = e.currentTarget as FocusableElement;

        if (e && !currentTarget.contains(e.target as Element)) {
          return;
        }

        if (e && e.button === 0) {
          e.stopPropagation();
          if (isDisabled) {
            e.preventDefault();
          }

          // If triggered from a screen reader or by using element.click(),
          // trigger as if it were a keyboard click.
          if (
            !state.ignoreClickAfterPress &&
            !state.ignoreEmulatedMouseEvents &&
            (state.pointerType === 'virtual' || isVirtualClick(e.nativeEvent))
          ) {
            if (!isDisabled) {
              focusWithoutScrolling(currentTarget);
            }

            triggerPressStart();
            triggerPressUp();
            triggerPressEnd();
          }

          state.ignoreEmulatedMouseEvents = false;
          state.ignoreClickAfterPress = false;
        }
      },
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (state.isPressed && isValidKeyboardEvent(e, stateTarget)) {
        if (shouldPreventDefaultKeyboard(e.target as Element, e.key)) {
          e.preventDefault();
        }
        e.stopPropagation();

        state.isPressed = false;
        const target = e.target as Element;
        triggerPressEnd(stateTarget.contains(target));
        removeAllGlobalListeners();

        // If the target is a link, trigger the click method to open the URL,
        // but defer triggering pressEnd until onClick event handler.
        if (
          state.target instanceof HTMLElement &&
          state.target.contains(target) &&
          (isHTMLAnchorLink(state.target) ||
            state.target.getAttribute('role') === 'link')
        ) {
          state.target.click();
        }
      }
    };

    pressProps.onPointerDown = (e) => {
      // Only handle left clicks, and ignore events that bubbled through portals.
      if (e.button !== 0 || !e.currentTarget.contains(e.target as Element)) {
        return;
      }

      // iOS safari fires pointer events from VoiceOver with incorrect coordinates/target.
      // Ignore and let the onClick handler take care of it instead.
      // https://bugs.webkit.org/show_bug.cgi?id=222627
      // https://bugs.webkit.org/show_bug.cgi?id=223202
      if (isVirtualPointerEvent(e.nativeEvent)) {
        state.pointerType = 'virtual';
        return;
      }

      // Due to browser inconsistencies, especially on mobile browsers, we prevent
      // default on pointer down and handle focusing the pressable element ourselves.
      if (shouldPreventDefault(e.currentTarget as Element)) {
        e.preventDefault();
      }

      state.pointerType = e.pointerType;

      if (!state.isPressed) {
        const currentTarget = e.currentTarget as FocusableElement;

        state.isPressed = true;
        state.isOverTarget = true;
        state.activePointerId = e.pointerId;
        state.target = currentTarget;
        stateTarget = state.target;

        if (!isDisabled) {
          focusWithoutScrolling(currentTarget);
        }

        disableTextSelection(stateTarget);
        triggerPressStart();

        addGlobalListener(document, 'pointerup', onPointerUp, false);
        addGlobalListener(
          document,
          'pointercancel',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onPointerCancel as any,
          false,
        );
      }
    };

    pressProps.onMouseDown = (e) => {
      if (!e.currentTarget.contains(e.target as Element)) {
        return;
      }

      if (e.button === 0) {
        // Chrome and Firefox on touch Windows devices require mouse down events
        // to be canceled in addition to pointer events, or an extra asynchronous
        // focus event will be fired.
        if (shouldPreventDefault(e.currentTarget as Element)) {
          e.preventDefault();
        }

        e.stopPropagation();
      }
    };

    pressProps.onPointerUp = (e) => {
      // iOS fires pointerup with zero width and height, so check the pointerType recorded during pointerdown.
      if (
        !e.currentTarget.contains(e.target as Element) ||
        state.pointerType === 'virtual'
      ) {
        return;
      }

      // Only handle left clicks
      // Safari on iOS sometimes fires pointerup events, even
      // when the touch isn't over the target, so double check.
      if (e.button === 0 && isOverTarget(e, e.currentTarget)) {
        triggerPressUp();
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (
        e.pointerId === state.activePointerId &&
        state.isPressed &&
        e.button === 0
      ) {
        if (isOverTarget(e, stateTarget)) {
          triggerPressEnd();
        } else if (state.isOverTarget) {
          triggerPressEnd(false);
        }

        state.isPressed = false;
        state.isOverTarget = false;
        state.activePointerId = null;
        state.pointerType = null;
        removeAllGlobalListeners();
        restoreTextSelection(stateTarget);
      }
    };

    const onPointerCancel = () => {
      cancel();
    };

    pressProps.onDragStart = (e) => {
      if (!e.currentTarget.contains(e.target as Element)) {
        return;
      }

      // Safari does not call onPointerCancel when a drag starts, whereas Chrome and Firefox do.
      cancel();
    };

    return pressProps;
  }, [addGlobalListener, isDisabled, removeAllGlobalListeners]);

  return {
    pressProps,
  };
}
